import { AxiosResponse } from 'axios';
import { createGroupRepositoryCore, GroupRepositoryCore } from '@/repositories/group-repository-core';
import {
    GroupCsvDownloadResult,
    GroupExtractResponse,
    GroupMergePayload,
    GroupMergeResponse,
    GroupRenamePayload,
    GroupRenameResponse,
} from '@/repositories/group-mutation-repository-handlers';
import { EndpointOptions } from '@/types/endpoint.options';

type GroupResourcePath = 'brands' | 'products';

type CreateMarketplaceGroupRepositoryBaseArgs = {
    resourcePath: GroupResourcePath;
    csvFileName: string;
};

export abstract class MarketplaceGroupRepositoryBase<
    TListResponse,
    TGroupDetailsResponse,
    TRenameResponse extends GroupRenameResponse,
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends GroupExtractResponse,
    TCsvQuery extends Record<string, unknown>,
> {
    private readonly core: GroupRepositoryCore<
        TListResponse,
        TGroupDetailsResponse,
        TRenameResponse,
        TExtractPayload,
        TExtractResponse,
        TCsvQuery
    >;

    constructor({ resourcePath, csvFileName }: CreateMarketplaceGroupRepositoryBaseArgs) {
        this.core = createGroupRepositoryCore<
            TListResponse,
            TGroupDetailsResponse,
            TRenameResponse,
            TExtractPayload,
            TExtractResponse,
            TCsvQuery
        >({
            resourcePath,
            csvFileName,
        });
    }

    protected get axios() {
        return this.core.axios;
    }

    protected getList(options?: EndpointOptions): Promise<AxiosResponse<TListResponse>> {
        return this.core.getList(options);
    }

    protected mergeGroups(
        data: GroupMergePayload,
        options?: EndpointOptions,
    ): Promise<AxiosResponse<GroupMergeResponse>> {
        return this.core.mergeGroups(data, options);
    }

    protected getGroup(groupId: string, options?: EndpointOptions): Promise<AxiosResponse<TGroupDetailsResponse>> {
        return this.core.getGroup(groupId, options);
    }

    protected renameGroup(
        groupId: string,
        data: GroupRenamePayload,
        options?: EndpointOptions,
    ): Promise<AxiosResponse<TRenameResponse>> {
        return this.core.renameGroup(groupId, data, options);
    }

    protected extractFromGroup(
        groupId: string,
        data: TExtractPayload,
        options?: EndpointOptions,
    ): Promise<AxiosResponse<TExtractResponse>> {
        return this.core.extractFromGroup(groupId, data, options);
    }

    protected downloadCsv(query: TCsvQuery, options?: EndpointOptions): Promise<GroupCsvDownloadResult> {
        return this.core.downloadCsv(query, options);
    }
}
